import React, { useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserService } from '../../services/user.service';
import { useToast } from '../../context/ToastContext';
import { displayUsername } from '../../utils/userUtils';

export interface UserOption {
    id: string;
    username: string;
}

/** Searches the institution's users as the admin types (debounced), like the task form's assignee picker. */
const useUserSearch = (institutionId: string, excludeId?: string) => {
    const toast = useToast();
    const { t } = useTranslation('appraisal');
    const [input, setInput] = useState('');
    const [options, setOptions] = useState<UserOption[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!input.trim()) {
            setOptions([]);
            return;
        }
        const handle = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await UserService.getInstitutionUsers(0, 20, institutionId, input);
                setOptions(((res?.content ?? []) as UserOption[])
                    .map(u => ({ id: u.id, username: u.username }))
                    .filter(u => u.id !== excludeId));
            } catch (err) {
                toast.showApiError(err, t('participants.errors.searchUsers'));
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [input, institutionId, excludeId]);

    return { setInput, options, loading };
};

const endAdornment = (loading: boolean, original: React.ReactNode) => (
    <>{loading ? <CircularProgress size={16} /> : null}{original}</>
);

interface SingleProps {
    institutionId: string;
    value: UserOption | null;
    onChange: (value: UserOption) => void;
    excludeId?: string;
    disabled?: boolean;
    ariaLabel: string;
    placeholder?: string;
}

/** One user, e.g. the reviewer of a review. */
export const SingleUserPicker: React.FC<SingleProps> = ({ institutionId, value, onChange, excludeId, disabled, ariaLabel, placeholder }) => {
    const { setInput, options, loading } = useUserSearch(institutionId, excludeId);
    const withValue = value && !options.some(o => o.id === value.id) ? [value, ...options] : options;
    return (
        <Autocomplete
            size="small"
            value={value}
            options={withValue}
            disabled={disabled}
            filterOptions={x => x}
            onChange={(_, v) => { if (v && v.id !== value?.id) onChange(v); }}
            onInputChange={(_, v, reason) => { if (reason === 'input') setInput(v); }}
            getOptionLabel={o => displayUsername(o.username)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={loading}
            sx={{ minWidth: 220 }}
            renderInput={params => (
                <TextField
                    {...params}
                    placeholder={placeholder}
                    inputProps={{ ...params.inputProps, 'aria-label': ariaLabel }}
                    InputProps={{ ...params.InputProps, endAdornment: endAdornment(loading, params.InputProps.endAdornment) }}
                />
            )}
        />
    );
};

interface MultiProps {
    institutionId: string;
    value: UserOption[];
    onChange: (value: UserOption[]) => void;
    label: string;
    helperText?: string;
}

/** Several users, e.g. hand-picked participants. */
export const MultiUserPicker: React.FC<MultiProps> = ({ institutionId, value, onChange, label, helperText }) => {
    const { setInput, options, loading } = useUserSearch(institutionId);
    return (
        <Autocomplete
            multiple
            value={value}
            options={options}
            filterOptions={x => x}
            onChange={(_, v) => onChange(v)}
            onInputChange={(_, v, reason) => { if (reason === 'input') setInput(v); }}
            getOptionLabel={o => displayUsername(o.username)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={loading}
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    helperText={helperText}
                    InputProps={{ ...params.InputProps, endAdornment: endAdornment(loading, params.InputProps.endAdornment) }}
                />
            )}
        />
    );
};
